import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { post, put } from "~/Services";
import { passengerHost } from "~/Utils";
import { fields } from "../constants";
import { useHelpHeadingDocumentDetails } from "./PageHeader.Hooks";

export const AddEditHeader = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const {
    data: editPageContent,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchCategories,
  } = useHelpHeadingDocumentDetails(query.get("editId") || "");

  const {
    data: editPageContentById,
    isLoading: isLoadingLocs,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("editId"), query.get("id")]);

  const formConfig: FormConfig = {
  initialValues: {
    title: editPageContentById
      ? decodeHTML(editPageContentById.payload?.title)
      : editPageContent
        ? decodeHTML(editPageContent?.title)
        : "",

    // 🔹 Image fields handled as Upload File List format
    field_page_banner_image: editPageContentById?.payload?.field_page_banner_image
      ? [
          {
            uid: "-1",
            name: "banner.jpg",
            status: "done",
            url: editPageContentById.payload.field_page_banner_image,
          },
        ]
      : editPageContent?.field_page_banner_image
      ? [
          {
            uid: "-1",
            name: "banner.jpg",
            status: "done",
            url: editPageContent.field_page_banner_image,
          },
        ]
      : [],

    field_page_header_metadata_image: editPageContentById?.payload?.field_page_header_metadata_image
      ? [
          {
            uid: "-2",
            name: "header_meta.jpg",
            status: "done",
            url: editPageContentById.payload.field_page_header_metadata_image,
          },
        ]
      : editPageContent?.field_page_header_metadata_image
      ? [
          {
            uid: "-2",
            name: "header_meta.jpg",
            status: "done",
            url: editPageContent.field_page_header_metadata_image,
          },
        ]
      : [],

    // 🔹 Text fields
    field_page_title_en: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_title_en)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_title_en)
        : "",

    field_page_title_ms: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_title_ms)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_title_ms)
        : "",

    field_page_title_zh: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_title_zh)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_title_zh)
        : "",

    field_page_description_en: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_description_en)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_description_en)
        : "",

    field_page_description_ms: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_description_ms)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_description_ms)
        : "",

    field_page_description_zh: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_description_zh)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_description_zh)
        : "",

    field_page_metadata_title_en: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_metadata_title_en)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_metadata_title_en)
        : "",

    field_page_metadata_title_ms: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_metadata_title_ms)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_metadata_title_ms)
        : "",

    field_page_metadata_title_zh: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_metadata_title_zh)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_metadata_title_zh)
        : "",

    field_page_meta_description_en: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_meta_description_en)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_meta_description_en)
        : "",

    field_page_meta_description_ms: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_meta_description_ms)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_meta_description_ms)
        : "",

    field_page_meta_description_zh: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_meta_description_zh)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_meta_description_zh)
        : "",

    field_page_header_airport: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_header_airport)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_header_airport)
        : "",

    field_page_header_name: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_header_name)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_header_name)
        : "",

    field_page_header_display_path: editPageContentById
      ? decodeHTML(editPageContentById.payload?.field_page_header_display_path)
      : editPageContent
        ? decodeHTML(editPageContent?.field_page_header_display_path)
        : "",

    status: editPageContentById
      ? editPageContentById.payload?.status
      : editPageContent
        ? editPageContent?.status
        : false,

    published: editPageContentById
      ? editPageContentById.payload?.published
      : editPageContent
        ? editPageContent?.published
        : false,
  },
  fields: fields.FlightInfoHeaders,
};


  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    console.log("data-123", data);

    let imageKeys: any = ["field_page_banner_image",
      "field_page_header_metadata_image"
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "PASSENGER");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    const payload = {
      action,
      method,
      module: "Page Header",
      subModule: "PageHeader",
      airportName: currentAirport,
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      reviewNotes,
      appList: [
        {
          appName: "PASSENGER_ECMS",
          apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && {
          id: editPageContent?.id || editPageContentById?.payload?.id,
          airports: currentAirport,
        }),
        ...data,
      },
      status,
    };

    console.log("Final Payload:", payload);

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post(`/management/publish`, payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Page Header`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingLocs}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") ? `${passengerHost}/api/create-page-headers-crud-data${editPageContent?.id || editPageContentById?.payload?.id}` : `${passengerHost}/api/create-page-headers-crud-data`}`,
            `${query.get("editId") ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </ContentWrapper>
  );
};
